/* A pack's reports, and the tasks behind every figure.
 *
 * Figures tie to records: a row of the table opens the list of tasks it was
 * made from, so nobody has to take a number on trust. The period is the
 * organisation's dates, as the server sent them.
 */
import { Fragment, useCallback, useEffect, useState } from 'react'
import { EmptyFirstTime, Failed, Loading, ScreenState } from '@jackdesk/shell'
import type { ErpError } from '../api'
import { useErp } from '../context'
import { formatNumber } from '../format'
import type { Kind, WorkReport, WorkReportListing } from './types'
import { usePersonName } from './Pickers'
import { usePacksActive } from './usePacks'
import { homeLabel, whoWord } from './words'

function dimensionLabel(dim: string, words: Kind['words'], measure?: string): string {
  switch (dim) {
    // Who held a stage can be anyone on the work; who worked the hours is the preparer.
    case 'person': return measure === 'stage_time' ? 'Person' : whoWord('maker', words)
    case 'stage': return 'Stage'
    case 'client': return 'Client'
    case 'month': return 'Month'
    case 'project': return 'Project'
    case 'maker': return whoWord('maker', words)
    case 'checker': return whoWord('checker', words)
    case 'approver': return whoWord('approver', words)
    case 'activity': return 'For'
    default: return dim
  }
}

const PEOPLE = new Set(['maker', 'person', 'checker', 'approver'])

function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number)
  if (!y || !m) return ym || '—'
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
}

export function WorkReports({ reportKey, onPick, onOpenTask, onBack }: {
  reportKey?: string
  onPick: (key: string) => void
  onOpenTask: (name: string) => void
  onBack: () => void
}) {
  const erp = useErp()
  const { active } = usePacksActive()
  const [list, setList] = useState<WorkReportListing[] | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)

  useEffect(() => {
    erp.workReports().then((r) => setList(r.reports)).catch((e) => setFailed(e as ErpError))
  }, [erp])

  if (failed) return <div className="page"><Failed message={failed.message} requestId={failed.requestId} /></div>
  if (!list) return <div className="page"><Loading rows={4} label="Reading the reports" /></div>

  return (
    <div className="page" style={{ display: 'grid', gap: 14, maxWidth: 1080 }}>
      <div>
        <button className="btn btn-quiet btn-sm" onClick={onBack}>← {homeLabel(active)}</button>
        <h1 style={{ marginTop: 6 }}>Work reports</h1>
      </div>
      {!list.length ? <EmptyFirstTime what="reports for the packs switched on here" /> : (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }} role="tablist" aria-label="Reports">
          {list.map((r) => (
            <button key={r.key} role="tab" aria-selected={r.key === reportKey}
                    className={`btn ${r.key === reportKey ? 'btn-primary' : ''}`} onClick={() => onPick(r.key)}>
              {r.title}
            </button>
          ))}
        </div>
      )}
      {reportKey ? <OneReport key={reportKey} reportKey={reportKey} onOpenTask={onOpenTask} />
        : list.length ? <ScreenState title="Pick a report" detail="Each figure opens the tasks it was made from." /> : null}
    </div>
  )
}

function OneReport({ reportKey, onOpenTask }: { reportKey: string; onOpenTask: (n: string) => void }) {
  const erp = useErp()
  const nameOf = usePersonName()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [report, setReport] = useState<WorkReport | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [open, setOpen] = useState<number | null>(null)

  const load = useCallback((signal?: AbortSignal) => {
    setFailed(null)
    erp.workReport(reportKey, from || undefined, to || undefined, signal)
      .then((r) => { setReport(r); setFrom(r.period.from); setTo(r.period.to); setOpen(null) })
      .catch((e) => { if ((e as Error).name !== 'AbortError') setFailed(e as ErpError) })
  // The period is applied with the button, not on every keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erp, reportKey])
  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  if (failed) return <Failed message={failed.message} requestId={failed.requestId} onRetry={() => load()} />
  if (!report) return <Loading rows={5} label="Working it out" />

  const show = (dim: string, value: string) =>
    !value ? '—' : dim === 'month' ? monthLabel(value) : PEOPLE.has(dim) ? nameOf(value) : value
  const unit = report.unit === '%' ? '%' : report.unit ? ` ${report.unit}` : ''
  const decimals = report.measure === 'on_time' ? 1 : report.unit === 'revisions' ? 0 : 2

  return (
    <section style={{ display: 'grid', gap: 10 }} aria-label={report.title}>
      <form className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}
            onSubmit={(e) => { e.preventDefault(); load() }}>
        <div className="field">
          <label htmlFor="rep_from">From</label>
          <input id="rep_from" type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="rep_to">To</label>
          <input id="rep_to" type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button className="btn">Show</button>
      </form>
      {report.about ? <p className="small muted" style={{ margin: 0, maxWidth: '70ch' }}>{report.about}</p> : null}
      {!report.rows.length ? (
        <ScreenState title="Nothing in this period" detail="Widen the dates, or check that work has been finished in them." />
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                {report.by.map((d) => <th key={d}>{dimensionLabel(d, report.words, report.measure)}</th>)}
                <th className="num">{report.measure === 'on_time' ? 'On time'
                  : report.unit ? report.unit.charAt(0).toUpperCase() + report.unit.slice(1) : 'Value'}</th>
                <th className="num">{report.measure === 'hours' ? 'Entries' : 'Tasks'}</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r, i) => (
                <Fragment key={i}>
                  <tr tabIndex={0} aria-expanded={open === i}
                      onClick={() => setOpen(open === i ? null : i)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setOpen(open === i ? null : i) }}>
                    {report.by.map((d) => <td key={d}>{show(d, r.group[d] ?? '')}</td>)}
                    <td className="num"><b>{formatNumber(r.value, decimals)}{unit}</b></td>
                    <td className="num">{r.count}</td>
                  </tr>
                  {open === i ? (
                    <tr style={{ cursor: 'default' }}>
                      <td colSpan={report.by.length + 2} style={{ background: 'var(--sunk)' }}>
                        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                          {r.tasks.map((t) => (
                            <button key={t} className="chip chip-info" style={{ cursor: 'pointer' }}
                                    onClick={() => onOpenTask(t)}>{t}</button>
                          ))}
                          {r.count > r.tasks.length ? <span className="small muted">and more</span> : null}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
              <tr style={{ cursor: 'default' }}>
                <td colSpan={report.by.length}><b>{report.measure === 'on_time' ? 'Overall' : 'Total'}</b></td>
                <td className="num"><b>{formatNumber(report.total, decimals)}{unit}</b></td>
                <td className="num">{report.rows.reduce((n, r) => n + r.count, 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
